import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { updateProgressNotes } from "../../actions/progress";
import { useRestoreFocus } from "../../hooks/useFocusTrap";

/** Convert user-written markdown+LaTeX note text into LaTeX source. */
function mdToLatex(md) {
  // Stash math blocks so their contents aren't touched by markdown rules.
  // $$...$$ must be matched before $...$ to avoid partial matches.
  const stash = [];
  const save = (m) => {
    stash.push(m);
    return `\x00M${stash.length - 1}\x00`;
  };
  let r = md.replace(/\$\$[\s\S]*?\$\$/g, save);
  r = r.replace(/\$[^$\n]+\$/g, save);

  // Markdown headings → paragraph-level so they don't clash with the
  // chapter/section structure that comes from EDCOMP_TITLE.
  r = r.replace(/^### (.+)$/gm, "\\subparagraph{$1}");
  r = r.replace(/^## (.+)$/gm, "\\paragraph{$1}");
  r = r.replace(/^# (.+)$/gm, "\\paragraph{$1}");

  // Bold before italic (** must be matched before *)
  r = r.replace(/\*\*([^*\n]+)\*\*/g, "\\textbf{$1}");
  r = r.replace(/\*([^*\n]+)\*/g, "\\textit{$1}");

  // Restore math blocks
  r = r.replace(/\x00M(\d+)\x00/g, (_, i) => stash[parseInt(i)]);

  return r;
}

const LEVEL_CMD = {
  chapter: "\\chapter",
  section: "\\section",
  subsection: "\\subsection",
};

/** Build a complete LaTeX document from all frames and their notes. */
function generateNotesLatex(content, notesAll, courseTitle) {
  const noteMap = {};
  notesAll.forEach((n) => {
    noteMap[String(n.frameId)] = n.note;
  });

  let body = "";
  (content ?? []).forEach((section) => {
    (section.subsections ?? []).forEach((subsection) => {
      (subsection.frames ?? []).forEach((frame) => {
        (frame.contents ?? []).forEach((item) => {
          if (item.type === "EDCOMP_TITLE") {
            const level = item.data?.level ?? "section";
            const cmd = LEVEL_CMD[level] ?? "\\section";
            const titleText = item.title.replace(/\\tag\{[^}]*\}/g, "").trim();
            body += `${cmd}{${titleText}}\n\n`;
          }
        });
        const note = noteMap[String(frame._id)];
        if (note?.trim()) {
          body += mdToLatex(note) + "\n\n";
        }
      });
    });
  });

  const docTitle = courseTitle ? `Notes: ${courseTitle}` : "My Notes";

  return `\\documentclass{report}
\\usepackage{amsmath}
\\usepackage{amssymb}
\\usepackage[margin=2.5cm]{geometry}
\\usepackage{parskip}
\\usepackage[hidelinks]{hyperref}

\\title{${docTitle}}
\\author{}
\\date{\\today}

\\begin{document}
\\maketitle
\\tableofcontents

${body.trim()}

\\end{document}
`;
}

/**
 * Per-frame notes panel with markdown+LaTeX editor and preview.
 * @param {{ frameId: string, onClose: () => void }} props
 */
export default function NotesPanel({ frameId, onClose }) {
  useRestoreFocus();
  const notesAll = useSelector((state) => state.ProgressReducer.notes);
  const content = useSelector((state) => state.ContentReducer.content);
  const courseTitle = useSelector(
    (state) => state.CourseStructureReducer.courseTitle,
  );
  const dispatch = useDispatch();
  const { id } = useParams();

  const existingNote = notesAll.find(
    (n) => String(n.frameId) === String(frameId),
  );
  const [text, setText] = useState(existingNote?.note ?? "");
  const [preview, setPreview] = useState(!!existingNote?.note?.trim());
  const previewRef = useRef(null);

  const saveToServer = useCallback(() => {
    const updated = notesAll.filter(
      (n) => String(n.frameId) !== String(frameId),
    );
    if (text.trim()) updated.push({ frameId, note: text });
    dispatch(updateProgressNotes(id, updated));
  }, [notesAll, text, frameId, id, dispatch]);

  // Typeset MathJax in preview
  useEffect(() => {
    if (!preview || !previewRef.current) return;
    const run = () => {
      if (window.MathJax?.typesetPromise) {
        window.MathJax.typesetPromise([previewRef.current]).catch(() => {});
      }
    };
    if (window.MathJax?.typesetPromise) run();
    else window.addEventListener("mathjax-ready", run, { once: true });
  }, [preview, text]);

  const handleDownload = () => {
    saveToServer();
    // Merge the currently-edited text into notesAll before generating,
    // since the server save above is async and Redux won't update in time.
    const mergedNotes = notesAll.filter(
      (n) => String(n.frameId) !== String(frameId),
    );
    if (text.trim()) mergedNotes.push({ frameId, note: text });
    const latex = generateNotesLatex(content, mergedNotes, courseTitle);
    const blob = new Blob([latex], { type: "text/x-latex" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "notes.tex";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Debounced autosave while typing
  useEffect(() => {
    const timer = setTimeout(saveToServer, 2500);
    return () => clearTimeout(timer);
  }, [text, saveToServer]);

  const handleClose = useCallback(() => {
    saveToServer();
    onClose();
  }, [saveToServer, onClose]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleClose]);

  /** Very basic markdown → HTML for preview */
  const renderMarkdown = (md) => {
    let h = md
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\\\$/g, "\x01DOLLAR\x01");

    h = h.replace(/^### (.+)$/gm, "<h3>$1</h3>");
    h = h.replace(/^## (.+)$/gm, "<h2>$1</h2>");
    h = h.replace(/^# (.+)$/gm, "<h1>$1</h1>");

    h = h.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    h = h.replace(/\*([^*]+)\*/g, "<em>$1</em>");

    h = h.replace(/\x01DOLLAR\x01/g, "$");
    h = h.replace(/\n\n/g, "</p><p>");
    h = h.replace(/\n/g, "<br>");

    return `<p>${h}</p>`;
  };

  return (
    <div className="np">
      <div className="np__header">
        <span className="np__title">Notes</span>
        <div className="np__header-actions">
          <button
            className={`np__tab ${!preview ? "np__tab--active" : ""}`}
            onClick={() => {
              saveToServer();
              setPreview(false);
            }}
          >
            Edit
          </button>
          <button
            className={`np__tab ${preview ? "np__tab--active" : ""}`}
            onClick={() => {
              saveToServer();
              setPreview(true);
            }}
          >
            Preview
          </button>
          <button
            className="np__download"
            onClick={handleDownload}
            title="Download all notes as LaTeX"
          >
            ↓
          </button>
          <button
            className="np__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {preview ? (
        <div
          ref={previewRef}
          className="np__preview"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }}
          onDoubleClick={() => setPreview(false)}
        />
      ) : (
        <textarea
          className="np__editor"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={saveToServer}
          placeholder="Write your notes on the highlighted section in Markdown. Use $...$ for inline math and $$...$$ for display math. Notes can be downloaded as LaTeX."
          spellCheck={false}
        />
      )}
    </div>
  );
}
